import { useState } from "react";
import { motion } from "framer-motion";

const DARK_IMG = "https://images.unsplash.com/photo-1733560175037-249f8954789b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxkYXJrJTIwY2hvY29sYXRlJTIwbHV4dXJ5JTIwcHJlbWl1bSUyMGFydGlzYW58ZW58MHx8fHwxNzc2MTc2Mzg3fDA&ixlib=rb-4.1.0&q=85";
const WHITE_IMG = "https://images.unsplash.com/photo-1635197026384-1696ac5e8f73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHx3aGl0ZSUyMGNob2NvbGF0ZSUyMGx1eHVyeSUyMGVsZWdhbnR8ZW58MHx8fHwxNzc2MTc2Mzk3fDA&ixlib=rb-4.1.0&q=85";

const ease = [0.16, 1, 0.3, 1];

export default function DualExperience() {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="dual-experience"
      data-testid="dual-experience-section"
      className="relative w-full"
    >
      {/* Section header */}
      <div className="text-center py-24 md:py-32 px-6">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease }}
          className="font-body text-xs md:text-sm uppercase tracking-[0.2em] text-[#C6A96B] mb-6"
        >
          The Dual Experience
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.2, ease }}
          className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#FDFBF7] mb-6"
        >
          One Box. Two Halves.
        </motion.h2>
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: 60 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4, ease }}
          className="h-px bg-[#C6A96B] mx-auto"
        />
      </div>

      {/* Split screen */}
      <div className="flex flex-col md:flex-row h-[50vh] md:h-[80vh] w-full overflow-hidden">
        {/* Dark side */}
        <motion.div
          data-testid="dual-dark-side"
          className="relative flex-1 overflow-hidden cursor-pointer group"
          animate={{ flex: hovered === "dark" ? 3 : hovered === "white" ? 2 : 1 }}
          transition={{ duration: 0.8, ease }}
          onMouseEnter={() => setHovered("dark")}
          onMouseLeave={() => setHovered(null)}
        >
          <img
            src={DARK_IMG}
            alt="Dark Chocolate"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-[#050505]/50 group-hover:bg-[#050505]/30 transition-all duration-700" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
            <motion.span
              className="font-body text-xs uppercase tracking-[0.3em] text-[#C6A96B] mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Intensity
            </motion.span>
            <motion.h3
              className="font-heading text-3xl md:text-5xl font-light text-[#FDFBF7] mb-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 1, ease }}
            >
              Noir
            </motion.h3>
            <motion.p
              className="font-body text-sm text-[#A3A3A3] font-light max-w-xs"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              72% single-origin dark, with notes of charred oak and black cherry
            </motion.p>
          </div>
          {/* Edge line */}
          <div className="absolute right-0 top-1/4 bottom-1/4 w-px bg-[#C6A96B]/20 hidden md:block" />
        </motion.div>

        {/* White side */}
        <motion.div
          data-testid="dual-white-side"
          className="relative flex-1 overflow-hidden cursor-pointer group"
          animate={{ flex: hovered === "white" ? 3 : hovered === "dark" ? 2 : 1 }}
          transition={{ duration: 0.8, ease }}
          onMouseEnter={() => setHovered("white")}
          onMouseLeave={() => setHovered(null)}
        >
          <img
            src={WHITE_IMG}
            alt="White Chocolate"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-[#050505]/50 group-hover:bg-[#050505]/30 transition-all duration-700" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
            <motion.span
              className="font-body text-xs uppercase tracking-[0.3em] text-[#C6A96B] mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Delicacy
            </motion.span>
            <motion.h3
              className="font-heading text-3xl md:text-5xl font-light text-[#FDFBF7] mb-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 1, ease }}
            >
              Ivoire
            </motion.h3>
            <motion.p
              className="font-body text-sm text-[#A3A3A3] font-light max-w-xs"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              Tahitian vanilla bean white, with whispers of jasmine and honeycomb
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
